/* eslint-disable react-refresh/only-export-components */
import type { ColumnDef } from "@tanstack/react-table";
import { Copy, MoreHorizontal, ToggleRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
      DataTableSortableHeader,
      DataTableStaticHeader,
      dataTablePrimaryTextClassName,
      dataTableTooltipUnderlineClassName,
} from "@/components/patterns/table-chrome";
import { EmptyCell } from "../table-row-components/empty-cell";
import { ProgressUnderline } from "../table-row-components/progress-underline";
import { RankingPill } from "../table-row-components/ranking-pill";
import { MentionRateTooltip } from "../tooltips/mention-rate-tooltip";
import { PromptBrandSpecificTooltip } from "../tooltips/prompt-brand-specific";
import { PromptCompetitorTooltip } from "../tooltips/prompt-competitor";
import { PromptOrganicTooltip } from "../tooltips/prompt-organic";
import { RankingTooltip } from "../tooltips/ranking-tooltip";
import { SentimentScoreTooltip } from "../tooltips/sentiment-score-tooltip";
import { VisibilityScoreTooltip } from "../tooltips/visibility-score-tooltip";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
      DropdownMenu,
      DropdownMenuContent,
      DropdownMenuItem,
      DropdownMenuLabel,
      DropdownMenuSeparator,
      DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { Skeleton } from "../ui/skeleton";

export interface PromptTableRow {
      id: string;
      text: string;
      promptType: "ORGANIC" | "COMPETITOR" | "BRAND_SPECIFIC";
      searchSpecificationId: string;
      status: "ACTIVE" | "INACTIVE";
      createdAt: string;
      updatedAt?: string | null;
      responseCount?: number;
      visibilityScore?: number;
      avgSentiment?: number | null;
      mentionRate?: number;
      averageRanking?: number | null;
      isMetricsLoading?: boolean;
}

const MetricSkeleton = () => <Skeleton className="mx-auto h-4 w-12" />;

type DeletePromptMutationLike = {
      mutate: (variables: { promptId: string }) => void;
};

type UpdatePromptStatusMutationLike = {
      mutate: (variables: { promptId: string; status: "ACTIVE" | "INACTIVE" }) => void;
};

// Create columns function that accepts the mutation
export const createColumns = (
      deletePromptMutation: DeletePromptMutationLike,
      updatePromptStatusMutation: UpdatePromptStatusMutationLike,
      handleRowClick?: (prompt: PromptTableRow) => void,
): ColumnDef<PromptTableRow>[] => [
      {
            accessorKey: "text",
            header: ({ column }) => <DataTableSortableHeader column={column}>Prompt Text</DataTableSortableHeader>,
            cell: ({ row }) => {
                  const text = row.getValue("text") as string;
                  const prompt = row.original;
                  const normalizedText = text.charAt(0).toUpperCase() + text.slice(1);
                  const displayText =
                        normalizedText.length > 50 ? `${normalizedText.slice(0, 50)}...` : normalizedText;

                  return (
                        <button
                              type="button"
                              className={`block w-full max-w-full min-w-0 appearance-none border-0 bg-transparent px-0 py-0 text-left shadow-none sm:max-w-[500px] ${
                                    prompt.id ? "cursor-pointer" : ""
                              }`}
                              onClick={(e) => {
                                    if (prompt.id && handleRowClick) {
                                          e.stopPropagation();
                                          handleRowClick(prompt);
                                    }
                              }}
                              tabIndex={prompt.id ? 0 : -1}
                        >
                              <Tooltip>
                                    <TooltipTrigger asChild>
                                          <p className={`${dataTablePrimaryTextClassName} line-clamp-2 break-words sm:line-clamp-1`}>
                                                {displayText}
                                          </p>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-[640px] whitespace-pre-wrap break-words leading-relaxed">
                                          {text}
                                    </TooltipContent>
                              </Tooltip>
                        </button>
                  );
            },
            filterFn: (row, id, value) => {
                  const text = row.getValue(id) as string;
                  return text.toLowerCase().includes(value.toLowerCase());
            },
      },
      {
            accessorKey: "promptType",
            header: ({ column }) => <DataTableSortableHeader column={column} align="center">Type</DataTableSortableHeader>,
            cell: ({ row }) => {
                  const type = row.getValue("promptType") as string;

                  const tooltipBadge = (
                        <Badge
                              variant={type as "ORGANIC" | "COMPETITOR" | "BRAND_SPECIFIC"}
                              className="capitalize cursor-help"
                        >
                              {type?.replace("_", " ").toLowerCase()}
                        </Badge>
                  );

                  return (
                        <div className="text-center flex items-center justify-center">
                              {type === "ORGANIC" ? (
                                    <PromptOrganicTooltip trigger={tooltipBadge} />
                              ) : type === "COMPETITOR" ? (
                                    <PromptCompetitorTooltip trigger={tooltipBadge} />
                              ) : (
                                    <PromptBrandSpecificTooltip trigger={tooltipBadge} />
                              )}
                        </div>
                  );
            },
      },
      {
            accessorKey: "visibilityScore",
            header: ({ column }) => (
                  <DataTableSortableHeader column={column} align="center">
                        <VisibilityScoreTooltip
                              trigger={<span>Visibility</span>}
                              triggerClassName={dataTableTooltipUnderlineClassName}
                        />
                  </DataTableSortableHeader>
            ),
            cell: ({ row }) => {
                  if (row.original.isMetricsLoading) {
                        return <MetricSkeleton />;
                  }

                  const score = (row.getValue("visibilityScore") as number | undefined) ?? 0;
                  const promptType = row.getValue("promptType") as string;
                  if (promptType !== "ORGANIC") {
                        return (
                              <div className="flex justify-center">
                                    <EmptyCell />
                              </div>
                        );
                  }
                  return (
                        <div className="flex justify-center">
                              <ProgressUnderline value={score} />
                        </div>
                  );
            },
      },
      {
            accessorKey: "avgSentiment",
            header: ({ column }) => (
                  <DataTableSortableHeader column={column} align="center">
                        <SentimentScoreTooltip
                              trigger={<span>Sentiment</span>}
                              triggerClassName={dataTableTooltipUnderlineClassName}
                        />
                  </DataTableSortableHeader>
            ),
            cell: ({ row }) => {
                  if (row.original.isMetricsLoading) {
                        return <MetricSkeleton />;
                  }

                  const score = (row.getValue("avgSentiment") as number | null | undefined) ?? 0;
                  return (
                        <div className="flex justify-center">
                              <ProgressUnderline value={score || 0} />
                        </div>
                  );
            },
      },
      {
            accessorKey: "mentionRate",
            header: ({ column }) => (
                  <DataTableSortableHeader column={column} align="center">
                        <MentionRateTooltip
                              trigger={<span>Mention</span>}
                              triggerClassName={dataTableTooltipUnderlineClassName}
                        />
                  </DataTableSortableHeader>
            ),
            cell: ({ row }) => {
                  if (row.original.isMetricsLoading) {
                        return <MetricSkeleton />;
                  }

                  const promptType = row.getValue("promptType") as string;
                  const rate = (row.getValue("mentionRate") as number | undefined) ?? 0;

                  // Only show mention rate for ORGANIC prompts
                  if (promptType !== "ORGANIC") {
                        return (
                              <div className="flex justify-center">
                                    {/* <ProgressUnderline value={100} muted /> */}
                                    <EmptyCell />
                              </div>
                        );
                  }

                  return (
                        <div className="flex justify-center">
                              <ProgressUnderline value={rate} />
                        </div>
                  );
            },
      },
      {
            accessorKey: "averageRanking",
            header: ({ column }) => (
                  <DataTableSortableHeader column={column} align="center">
                        <RankingTooltip
                              trigger={<span>Rank</span>}
                              triggerClassName={dataTableTooltipUnderlineClassName}
                        />
                  </DataTableSortableHeader>
            ),
            cell: ({ row }) => {
                  if (row.original.isMetricsLoading) {
                        return <MetricSkeleton />;
                  }

                  const ranking = (row.getValue("averageRanking") as number | null | undefined) ?? null;

                  return (
                        <div className="flex justify-center">
                              {ranking && ranking > 0 ? <RankingPill ranking={ranking} /> : <EmptyCell />}
                        </div>
                  );
            },
      },
      {
            accessorKey: "responseCount",
            header: ({ column }) => <DataTableSortableHeader column={column} align="center">Responses</DataTableSortableHeader>,
            cell: ({ row }) => {
                  if (row.original.isMetricsLoading) {
                        return <MetricSkeleton />;
                  }

                  const count = (row.getValue("responseCount") as number | undefined) ?? 0;
                  return <div className="text-center">{count || 0}</div>;
            },
      },
      {
            id: "actions",
            enableHiding: false,
            header: () => <DataTableStaticHeader align="center">Actions</DataTableStaticHeader>,
            cell: ({ row }) => {
                  const prompt = row.original;

                  return (
                        <div className="text-center">
                              <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                          <Button variant="utility" size="iconXs">
                                                <span className="sr-only">Open menu</span>
                                                <MoreHorizontal className="h-4 w-4" />
                                          </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                          <DropdownMenuItem
                                                className="cursor-pointer"
                                                onClick={async () => {
                                                      await navigator.clipboard.writeText(prompt.text);
                                                      toast.success("Prompt text copied to clipboard");
                                                }}
                                          >
                                                <Copy className="mr-2 h-4 w-4" />
                                                Copy prompt text
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                                className="cursor-pointer"
                                                onClick={() =>
                                                      updatePromptStatusMutation.mutate({
                                                            promptId: prompt.id,
                                                            status: prompt.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
                                                      })
                                                }
                                          >
                                                <ToggleRight className="mr-2 h-4 w-4" />
                                                {prompt.status === "ACTIVE" ? "Deactivate" : "Activate"} prompt
                                          </DropdownMenuItem>
                                          <DropdownMenuSeparator />
                                          <DropdownMenuItem
                                                variant="destructive"
                                                onClick={() => deletePromptMutation.mutate({ promptId: prompt.id })}
                                          >
                                                <Trash2 className="mr-2 h-4 w-4" />
                                                Archive prompt
                                          </DropdownMenuItem>
                                    </DropdownMenuContent>
                              </DropdownMenu>
                        </div>
                  );
            },
      },
];
